import {
    takeLatest,
    put,
    call,
    select,
} from 'redux-saga/effects';

// endpoints
import {
    USER_MANAGEMENT,
    AUTHENTICATION,
} from "../../../components/global/Server/endpoints";

// constants for actions
import {
    FETCH_LINKED_USERS,
    EDIT_USER, DELETE_USER,
    GET_ROLES, FETCH_LOCATION_DETAIL,
} from '../../constants/userManagement';

// secondary actions
import {
    storeLinkedUsers, storeLocationDetail,
    storeRoles,
} from '../../actions/userManagement';
import {
    requestStatus,
    setLoadingStatus,
    clearStatus,
    showErrorMessage
} from "../../actions/global";
import { updateUserInfo } from "../../actions/auth";
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../pages/Dashboard/constants';
import AuthUtil from "../../AuthUtil";


function* fetchLinkedUsers(action) {
    yield call(clearStatus)
    yield put(setLoadingStatus(true))
    try {
        const response = yield call(() => AuthUtil.postRequest(USER_MANAGEMENT.FIND_USERS, action.username))
        if (response.status === 200) {
            yield put(storeLinkedUsers(response.data))
        }
        const status = {
            status: response.status,
            statusText: response.statusText,
            success: response.status === 200,
        }
        yield put(requestStatus(status))
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE))
    } finally {
        yield put(setLoadingStatus(false))
    }
}


function* editUser(action) {
    yield call(clearStatus)
    yield put(setLoadingStatus(true))
    try {

        const response = yield call(() => AuthUtil.putRequest(USER_MANAGEMENT.UPDATE_USER, action.userInfo))
        const status = {
            status: response.status,
            statusText: response.statusText,
            success: response.status === 200,
        }
        yield put(requestStatus(status))
        yield put(setLoadingStatus(false))
        action.setState(true)

        if (status.success) {
            // If the user just edited their own account (e.g. linked a new
            // location to themselves), the picker is driven by
            // userInfo.companyIds / selectedLocations / routes which won't
            // contain the newly linked location until /account/getUser is
            // called again. Refresh from the backend so the picker updates
            // immediately instead of requiring a logout/login.
            let mergedUserInfo = action.updatedUserInfo;
            const currentUserInfo = yield select(state => state.auth.userInfo);
            if (
                currentUserInfo &&
                action.userInfo &&
                action.userInfo.user_to_change === currentUserInfo.username
            ) {
                try {
                    const refresh = yield call(() =>
                        AuthUtil.postRequest(
                            AUTHENTICATION.GET_USER_INFO,
                            { username: currentUserInfo.username }
                        )
                    );
                    if (refresh.status === 200 && refresh.data) {
                        mergedUserInfo = {
                            ...(action.updatedUserInfo || currentUserInfo),
                            companyIds: refresh.data.companyIds,
                            selectedLocations: refresh.data.selectedLocations,
                            routes: refresh.data.routes,
                            linkedUsers: refresh.data.linkedUsers,
                            // An owner editing their OWN access level needs
                            // it applied straight away, same as routes —
                            // otherwise the app keeps enforcing the old mode
                            // until the next login.
                            accessMode: refresh.data.accessMode || 'FULL',
                        };
                        localStorage.setItem(
                            'accessMode',
                            refresh.data.accessMode || 'FULL'
                        );
                    }
                } catch (e) {
                    // Fall back to the optimistic update below.
                }
            }

            yield put(updateUserInfo(mergedUserInfo));
            const userAction = {
                type: FETCH_LINKED_USERS,
                username: {
                    locationId: action.userInfo.locationId
                }
            }

            yield call(() => fetchLinkedUsers(userAction))
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE))
    } finally {
        yield put(setLoadingStatus(false))
    }
}

function* deleteUser(action) {
    yield call(clearStatus)
    try {
        const response = yield call(() => AuthUtil.postRequest(USER_MANAGEMENT.DELETE_USER, action.userInfo))

        const status = {
            status: response.status,
            statusText: response.statusText,
            success: response.status === 200,
        }
        yield put(requestStatus(status))
        yield put(setLoadingStatus(false))
        action.setState(true)

        if (status.success) {
            const userAction = {
                type: FETCH_LINKED_USERS,
                username: {
                    locationId: action.userInfo.locationId
                }
            }
            yield call(() => fetchLinkedUsers(userAction))
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE))
    }
}

function* getRolesSaga(action) {
    try {
        const response = yield call(() => AuthUtil.postRequest(USER_MANAGEMENT.GET_ROLES,action.data))
        const status = {
            status: response.status,
            statusText: response.statusText,
            success: response.status === 200,
        };
        yield put(storeRoles(response.data));
        yield put(requestStatus(status));
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE))
    }
}

function* fetchLocationDetailSaga(action) {
    yield call(clearStatus)
    yield put(setLoadingStatus(true))
    try {
        const response = yield call(() => AuthUtil.postRequest(USER_MANAGEMENT.GET_LOCATION_DETAIL, action.data))
        if (response.status === 200) {
            yield put(storeLocationDetail(response.data.data))
        }
        const status = {
            status: response.status,
            statusText: response.statusText,
            success: response.status === 200,
        }
        yield put(requestStatus(status))
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE))
    } finally {
        yield put(setLoadingStatus(false))
    }
}

export default function* userManagementSaga() {
    yield takeLatest(FETCH_LINKED_USERS, fetchLinkedUsers);
    yield takeLatest(EDIT_USER, editUser);
    yield takeLatest(DELETE_USER, deleteUser)
    yield takeLatest(GET_ROLES, getRolesSaga)
    yield takeLatest(FETCH_LOCATION_DETAIL, fetchLocationDetailSaga)
}

